/**
 * Procedural Game Event Sound Generators
 * 
 * Creates sounds for major game events:
 * - End Turn
 * - Combat (initiated, victory, defeat)
 * - Territory capture
 * - Player elimination
 * - Resource/Tech events
 */

import { PROCEDURAL_PARAMS } from '../audioConfig.js';

/**
 * Main event sound dispatcher
 * @param {AudioContext} ctx - Web Audio context
 * @param {GainNode} destination - Destination gain node
 * @param {string} eventName - Event name from EVENT_SOUNDS
 */
export function createEventSound(ctx, destination, eventName) {
  if (!ctx || !destination) return;

  switch (eventName) {
    case 'endTurn':
      createEndTurnSound(ctx, destination);
      break;
    case 'combatInitiated':
      createCombatInitiatedSound(ctx, destination);
      break;
    case 'combatVictory':
      createCombatVictorySound(ctx, destination);
      break;
    case 'combatDefeat':
      createCombatDefeatSound(ctx, destination);
      break;
    case 'combatStalemate':
      createCombatStalemateSound(ctx, destination);
      break;
    case 'territoryCaptured':
      createTerritoryCapturedSound(ctx, destination);
      break;
    case 'territoryLost':
      createTerritoryLostSound(ctx, destination);
      break;
    case 'playerEliminated':
      createPlayerEliminatedSound(ctx, destination);
      break;
    case 'resourceGained':
      createResourceGainedSound(ctx, destination);
      break;
    case 'techUnlocked':
      createTechUnlockedSound(ctx, destination);
      break;
    default:
      console.warn(`[Events] Unknown event sound: ${eventName}`);
  }
}

/**
 * End Turn - Satisfying whoosh/transition sound
 */
function createEndTurnSound(ctx, destination) {
  const params = PROCEDURAL_PARAMS.endTurn;
  const now = ctx.currentTime;

  // Main sweep
  const oscillator = ctx.createOscillator();
  oscillator.type = params.type;
  oscillator.frequency.setValueAtTime(params.startFrequency, now);
  oscillator.frequency.exponentialRampToValueAtTime(
    params.endFrequency,
    now + params.duration
  );

  // Add subtle noise for texture
  const noiseBuffer = createNoiseBuffer(ctx, 0.5);
  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = noiseBuffer;

  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0, now);
  noiseGain.gain.linearRampToValueAtTime(params.noiseAmount, now + 0.05);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + params.duration);

  // Filter the noise
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.setValueAtTime(400, now);
  noiseFilter.frequency.linearRampToValueAtTime(1200, now + params.duration);
  noiseFilter.Q.value = 1;

  // Main oscillator envelope
  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(0.25, now + 0.02);
  gainNode.gain.setValueAtTime(0.25, now + params.duration * 0.6);
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + params.duration);

  // Connect main
  oscillator.connect(gainNode);
  gainNode.connect(destination);

  // Connect noise
  noiseSource.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(destination);

  oscillator.start(now);
  oscillator.stop(now + params.duration + 0.01);
  noiseSource.start(now);
  noiseSource.stop(now + params.duration + 0.01);

  oscillator.onended = () => {
    oscillator.disconnect();
    gainNode.disconnect();
    noiseSource.disconnect();
    noiseFilter.disconnect();
    noiseGain.disconnect();
  };
}

/**
 * Combat Initiated - Dramatic low brass-like sound
 */
function createCombatInitiatedSound(ctx, destination) {
  const params = PROCEDURAL_PARAMS.combatInitiated;
  const now = ctx.currentTime;

  // Create layered harmonics for brass-like timbre
  const harmonics = params.harmonics || [1, 0.5, 0.25];
  
  harmonics.forEach((amplitude, index) => {
    const oscillator = ctx.createOscillator();
    oscillator.type = index === 0 ? 'sawtooth' : 'sine';
    oscillator.frequency.setValueAtTime(
      params.frequency * (index + 1),
      now
    );

    const gainNode = ctx.createGain();
    const baseGain = 0.15 * amplitude;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(baseGain, now + 0.03);
    gainNode.gain.setValueAtTime(baseGain, now + params.duration * 0.5);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + params.duration);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(800, now);

    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(destination);

    oscillator.start(now);
    oscillator.stop(now + params.duration + 0.01);

    oscillator.onended = () => {
      oscillator.disconnect();
      filter.disconnect();
      gainNode.disconnect();
    };
  });
}

/**
 * Combat Victory - Triumphant ascending fanfare
 */
function createCombatVictorySound(ctx, destination) {
  const params = PROCEDURAL_PARAMS.combatVictory;
  const now = ctx.currentTime;

  params.frequencies.forEach((freq, index) => {
    const startTime = now + (index * params.stagger);

    // Main note
    const oscillator = ctx.createOscillator();
    oscillator.type = params.type;
    oscillator.frequency.setValueAtTime(freq, startTime);

    // Add shimmer with slight detune
    const oscillator2 = ctx.createOscillator();
    oscillator2.type = 'sine';
    oscillator2.frequency.setValueAtTime(freq * 1.003, startTime);

    const gainNode = ctx.createGain();
    const volume = 0.18 - (index * 0.02);
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.015);
    gainNode.gain.setValueAtTime(volume, startTime + params.duration * 0.4);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + params.duration);

    const gainNode2 = ctx.createGain();
    gainNode2.gain.setValueAtTime(0, startTime);
    gainNode2.gain.linearRampToValueAtTime(volume * 0.3, startTime + 0.015);
    gainNode2.gain.exponentialRampToValueAtTime(0.001, startTime + params.duration);

    oscillator.connect(gainNode);
    oscillator2.connect(gainNode2);
    gainNode.connect(destination);
    gainNode2.connect(destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + params.duration + 0.01);
    oscillator2.start(startTime);
    oscillator2.stop(startTime + params.duration + 0.01);

    oscillator.onended = () => {
      oscillator.disconnect();
      oscillator2.disconnect();
      gainNode.disconnect();
      gainNode2.disconnect();
    };
  });
}

/**
 * Combat Defeat - Somber descending minor progression
 */
function createCombatDefeatSound(ctx, destination) {
  const params = PROCEDURAL_PARAMS.combatDefeat;
  const now = ctx.currentTime;

  params.frequencies.forEach((freq, index) => {
    const startTime = now + (index * params.stagger);

    const oscillator = ctx.createOscillator();
    oscillator.type = params.type;
    oscillator.frequency.setValueAtTime(freq, startTime);
    // Slight downward bend
    oscillator.frequency.linearRampToValueAtTime(
      freq * 0.97,
      startTime + params.duration
    );

    const gainNode = ctx.createGain();
    const volume = 0.15;
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + params.duration);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1200, startTime);
    filter.frequency.linearRampToValueAtTime(400, startTime + params.duration);

    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + params.duration + 0.01);

    oscillator.onended = () => {
      oscillator.disconnect();
      filter.disconnect();
      gainNode.disconnect();
    };
  });
}

/**
 * Territory Captured - Quick triumphant blip
 */
function createTerritoryCapturedSound(ctx, destination) {
  const params = PROCEDURAL_PARAMS.territoryCaptured;
  const now = ctx.currentTime;

  params.frequencies.forEach((freq, index) => {
    const startTime = now + (index * params.stagger);

    const oscillator = ctx.createOscillator();
    oscillator.type = params.type;
    oscillator.frequency.setValueAtTime(freq, startTime);

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(0.2, startTime + 0.008);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + params.duration);

    oscillator.connect(gainNode);
    gainNode.connect(destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + params.duration + 0.01);

    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  });
}

/**
 * Player Eliminated - Dramatic low drone
 */
function createPlayerEliminatedSound(ctx, destination) {
  const params = PROCEDURAL_PARAMS.playerEliminated;
  const now = ctx.currentTime;

  // Deep descending drone
  const oscillator = ctx.createOscillator();
  oscillator.type = params.type;
  oscillator.frequency.setValueAtTime(params.frequency, now);
  oscillator.frequency.exponentialRampToValueAtTime(
    params.endFrequency,
    now + params.duration
  );

  // Sub bass
  const subOsc = ctx.createOscillator();
  subOsc.type = 'sine';
  subOsc.frequency.setValueAtTime(params.frequency / 2, now);
  subOsc.frequency.exponentialRampToValueAtTime(
    params.endFrequency / 2,
    now + params.duration
  );

  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(0.2, now + 0.05);
  gainNode.gain.setValueAtTime(0.2, now + params.duration * 0.5);
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + params.duration);

  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0, now);
  subGain.gain.linearRampToValueAtTime(0.15, now + 0.05);
  subGain.gain.exponentialRampToValueAtTime(0.001, now + params.duration);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(600, now);
  filter.frequency.linearRampToValueAtTime(200, now + params.duration);

  oscillator.connect(filter);
  filter.connect(gainNode);
  subOsc.connect(subGain);
  gainNode.connect(destination);
  subGain.connect(destination);

  oscillator.start(now);
  oscillator.stop(now + params.duration + 0.01);
  subOsc.start(now);
  subOsc.stop(now + params.duration + 0.01);

  oscillator.onended = () => {
    oscillator.disconnect();
    subOsc.disconnect();
    filter.disconnect();
    gainNode.disconnect();
    subGain.disconnect();
  };
}

/**
 * Resource Gained - Quick pleasant ping
 */
function createResourceGainedSound(ctx, destination) {
  const params = PROCEDURAL_PARAMS.resourceGained;
  const now = ctx.currentTime;

  const oscillator = ctx.createOscillator();
  oscillator.type = params.type;
  oscillator.frequency.setValueAtTime(params.frequency, now);

  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(0.15, now + 0.005);
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + params.duration);

  oscillator.connect(gainNode);
  gainNode.connect(destination);

  oscillator.start(now);
  oscillator.stop(now + params.duration + 0.01);

  oscillator.onended = () => {
    oscillator.disconnect();
    gainNode.disconnect();
  };
}

/**
 * Tech Unlocked - Sci-fi ascending sweep
 */
function createTechUnlockedSound(ctx, destination) {
  const params = PROCEDURAL_PARAMS.techUnlocked;
  const now = ctx.currentTime;

  params.frequencies.forEach((freq, index) => {
    const startTime = now + (index * params.stagger);

    const oscillator = ctx.createOscillator();
    oscillator.type = params.type;
    oscillator.frequency.setValueAtTime(freq, startTime);

    // Shimmer effect
    const oscillator2 = ctx.createOscillator();
    oscillator2.type = 'sine';
    oscillator2.frequency.setValueAtTime(freq * 2.01, startTime);

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(0.12, startTime + 0.008);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + params.duration);

    const gainNode2 = ctx.createGain();
    gainNode2.gain.setValueAtTime(0, startTime);
    gainNode2.gain.linearRampToValueAtTime(0.05, startTime + 0.01);
    gainNode2.gain.exponentialRampToValueAtTime(0.001, startTime + params.duration);

    oscillator.connect(gainNode);
    oscillator2.connect(gainNode2);
    gainNode.connect(destination);
    gainNode2.connect(destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + params.duration + 0.01);
    oscillator2.start(startTime);
    oscillator2.stop(startTime + params.duration + 0.01);

    oscillator.onended = () => {
      oscillator.disconnect();
      oscillator2.disconnect();
      gainNode.disconnect();
      gainNode2.disconnect();
    };
  });
}

/**
 * Combat Stalemate - Neutral, uncertain sound (neither victory nor defeat)
 */
function createCombatStalemateSound(ctx, destination) {
  const now = ctx.currentTime;
  const duration = 0.5;

  // Two oscillators creating a subtle tension
  const osc1 = ctx.createOscillator();
  const osc2 = ctx.createOscillator();
  
  osc1.type = 'sine';
  osc2.type = 'sine';
  
  // Minor second interval - slight dissonance
  osc1.frequency.setValueAtTime(220, now);
  osc2.frequency.setValueAtTime(233, now); // Slightly sharp
  
  const gain1 = ctx.createGain();
  const gain2 = ctx.createGain();
  
  gain1.gain.setValueAtTime(0, now);
  gain1.gain.linearRampToValueAtTime(0.12, now + 0.05);
  gain1.gain.setValueAtTime(0.12, now + duration * 0.6);
  gain1.gain.exponentialRampToValueAtTime(0.001, now + duration);
  
  gain2.gain.setValueAtTime(0, now);
  gain2.gain.linearRampToValueAtTime(0.08, now + 0.08);
  gain2.gain.setValueAtTime(0.08, now + duration * 0.6);
  gain2.gain.exponentialRampToValueAtTime(0.001, now + duration);
  
  // Low pass filter for muffled sound
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 800;
  
  osc1.connect(gain1);
  osc2.connect(gain2);
  gain1.connect(filter);
  gain2.connect(filter);
  filter.connect(destination);
  
  osc1.start(now);
  osc2.start(now);
  osc1.stop(now + duration + 0.01);
  osc2.stop(now + duration + 0.01);
  
  osc1.onended = () => {
    osc1.disconnect();
    osc2.disconnect();
    gain1.disconnect();
    gain2.disconnect();
    filter.disconnect();
  };
}

/**
 * Territory Lost - Somber, descending sound
 */
function createTerritoryLostSound(ctx, destination) {
  const now = ctx.currentTime;
  const duration = 0.6;

  // Descending tone
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(330, now);
  osc.frequency.exponentialRampToValueAtTime(165, now + duration);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.15, now + 0.03);
  gain.gain.setValueAtTime(0.15, now + duration * 0.4);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  // Add slight reverb-like effect with filtered noise
  const noiseBuffer = createNoiseBuffer(ctx, duration);
  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = noiseBuffer;

  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0, now);
  noiseGain.gain.linearRampToValueAtTime(0.02, now + 0.1);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'lowpass';
  noiseFilter.frequency.value = 400;

  osc.connect(gain);
  gain.connect(destination);

  noiseSource.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(destination);

  osc.start(now);
  osc.stop(now + duration + 0.01);
  noiseSource.start(now);
  noiseSource.stop(now + duration + 0.01);

  osc.onended = () => {
    osc.disconnect();
    gain.disconnect();
    noiseSource.disconnect();
    noiseFilter.disconnect();
    noiseGain.disconnect();
  };
}

// =====================================================================
// UTILITY: Create noise buffer
// =====================================================================

function createNoiseBuffer(ctx, duration) {
  const sampleRate = ctx.sampleRate;
  const bufferSize = sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, sampleRate);
  const data = buffer.getChannelData(0);

  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  return buffer;
}
